import type { DRDayFixture, DRDaySummary, PickemItem, SquaresMyPick } from '../api/getMyGames';
import type { SurvivorMyGame } from './lobbyMyGamesSurvivor';

/**
 * My Games sub-tabs.
 *
 * `recent` is the bucket previously labelled "Past Results". The id changed with the label so
 * the URL reads the same as the chip; nothing persisted the old `past` value, so there is no
 * migration to do.
 */
export type MyGamesTab = 'upcoming' | 'live' | 'recent';

export const MY_GAMES_TABS: Array<{ id: MyGamesTab; label: string }> = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'live', label: 'Live' },
  { id: 'recent', label: 'Recent' },
];

export const DEFAULT_MY_GAMES_TAB: MyGamesTab = 'upcoming';

/** Search-param key carrying the active sub-tab (`?tab=live`). */
export const MY_GAMES_TAB_PARAM = 'tab';

/** Narrow an untrusted URL value to a tab, falling back to the default. */
export function parseMyGamesTab(value: string | null | undefined): MyGamesTab {
  const match = MY_GAMES_TABS.find(tab => tab.id === value);
  return match ? match.id : DEFAULT_MY_GAMES_TAB;
}

// ─── Live / upcoming split ────────────────────────────────────────────────────
//
// The three tabs partition the contests the user holds an entry in:
//
//   Upcoming — entered, and the game has not started yet
//   Live     — entered, and the game has started
//   Recent   — the contest has finished
//
// Membership ("entered") is settled upstream, by the query each game type is bucketed from;
// Recent is settled by the existing `is…Complete` tests. What was left is the Upcoming/Live
// line, which is what the predicates below draw: each returns true once its game type has
// actually started, and "upcoming" is the remainder.
//
// They share a two-signal shape — the status the feed writes, plus the kick-off time as a
// backstop — because a status write can lag the whistle, and a contest already on screen must
// not still be filed under Upcoming. Keeping them in one module is what stops the four game
// types from drifting apart on the meaning of "live", the way their status strings already
// have.

function toTime(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

export function isSquaresPickLive(pick: SquaresMyPick, now: number = Date.now()): boolean {
  const status = (pick.status ?? '').toUpperCase();
  // The statuses the Squares card paints its own LIVE pill on.
  if (status === 'ACTIVE' || pick.status === 'live') return true;
  // …and the kick-off time as a backstop, matching Pick'em. Squares contests are flipped to
  // ACTIVE by the feed, so between kick-off and that write a started contest still reads OPEN;
  // without this it would sit under Upcoming while the game is on screen.
  const start = toTime(pick.start_time_utc);
  return start > 0 && start <= now;
}

export function isPickemLive(pickem: PickemItem, now: number = Date.now()): boolean {
  const status = String(pickem.status ?? '').toUpperCase();
  if (status === 'INPLAY' || status === 'LIVE' || status === '1') return true;
  const start = pickem.start
    ? toTime(pickem.start as string)
    : pickem.start_utc_timestamp
      ? (pickem.start_utc_timestamp as number) * 1000
      : 0;
  // A start time that has passed means live even while the status still says OPEN — the
  // Pick'em card resolves its own state the same way.
  return start > 0 && start <= now;
}

/**
 * Has the CONTEST begun? Not "is the user still in it" — that question is settled earlier, by
 * `isSurvivorEntryOver`, which routes an eliminated entrant to Recent before this predicate is
 * ever reached. Everything that gets here is an entry still in play.
 */
export function isSurvivorGameLive(game: SurvivorMyGame, now: number = Date.now()): boolean {
  // A week that locked or graded is proof the contest is under way, and it is the only signal
  // a season-long contest reliably carries — `pickem_status` is free text and `start` can be
  // absent on contests assembled from the tenant list alone.
  if (game.weeks.some(week => week.status === 'locked' || week.status === 'graded')) return true;
  const status = String(game.pickemStatus ?? '').toUpperCase();
  if (status === 'INPLAY' || status === 'LIVE' || status === '1') return true;
  const start = toTime(game.startUtc);
  return start > 0 && start <= now;
}

/**
 * The Sportradar fixture for the club on the user's selected card, when the day's
 * fixture list is available. My Games tabs key off this game — not the reveal
 * pack lifecycle — so an NFL Sunday night kickoff stays in Upcoming until the
 * feed flips it live, even if the day's reveal already closed.
 */
export function findDailyRevealPlayerFixture(
  day: DRDaySummary,
  fixtures: DRDayFixture[]
): DRDayFixture | null {
  const teamId = day.userEntry?.selectedCard?.teamExternalId;
  if (!teamId || fixtures.length === 0) return null;
  return fixtures.find(f => f.homeTeamId === teamId || f.awayTeamId === teamId) ?? null;
}

/**
 * Partition a Daily Reveal day across Upcoming / Live / Recent.
 *
 * Prefer the Sportradar fixture for the player's game (parity with Squares and
 * Pick'em). Fall back to the reveal day lifecycle only while fixtures have not
 * loaded yet — otherwise a locked NFL day with a still-scheduled kickoff was
 * filed under Live/Recent instead of Upcoming.
 */
export function classifyDailyRevealDayTab(
  day: DRDaySummary,
  fixtures: DRDayFixture[] | null | undefined,
  now: number = Date.now()
): MyGamesTab {
  const fixture =
    fixtures && fixtures.length > 0 ? findDailyRevealPlayerFixture(day, fixtures) : null;

  if (fixture) {
    if (fixture.status === 'finished' || fixture.status === 'cancelled') return 'recent';
    if (fixture.status === 'live') return 'live';
    // scheduled / postponed — kick-off backstop matches Squares/Pick'em
    const start = toTime(fixture.startTime);
    if (start > 0 && start <= now) return 'live';
    return 'upcoming';
  }

  if (day.status === 'graded' || day.userEntry?.status === 'graded') return 'recent';
  if (day.status === 'locked') return 'live';
  if (day.status === 'open' || day.userEntry?.selectedCard) return 'upcoming';
  return 'recent';
}

/**
 * A Daily Reveal day is live once the player's Sportradar fixture is in play
 * (or kick-off has passed). Without fixtures, fall back to a locked reveal day.
 */
export function isDailyRevealDayLive(
  day: DRDaySummary | null | undefined,
  fixtures?: DRDayFixture[] | null,
  now: number = Date.now()
): boolean {
  if (!day) return false;
  return classifyDailyRevealDayTab(day, fixtures, now) === 'live';
}
